import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import ReferenceSelect from "../../listings/components/ReferenceSelect.jsx";
import { AssignPersonPicker } from "../../listingFlows/FlowKit.jsx";
import {
  INTENTS,
  INTENT_LABELS,
  INTENT_ACTION,
  MAX_PER_SIDE,
} from "../constants/requestDictation";
import { conditionOptions, boxOptions } from "../../product/constants";
import { searchClients } from "../services/requestDictation";
import { rankClientMatches, personName } from "../services/clientMatch";
import { UserContext } from "../../contexts";
import "./request-dictation-review.scss";

/**
 * The editable review inside the request dictation sheet.
 *
 * Reads in the order the flow itself teaches — CLIENT, then the PRODUCTS, then
 * the fields — because the purpose-built variants hoist ASSIGN CLIENT first
 * (`sectionFirst` in flowVariants.js) and a sheet that taught a different order
 * would be a second thing to learn.
 *
 * Everything is a small card. A part exchange can carry up to three watches a
 * side, and a flat form of six watches' worth of selects is unreadable on a
 * laptop and impossible on a phone. A card collapses to one line once its
 * product is confirmed, so the panel shrinks as the rep works through it.
 */

const YEARS = (() => {
  const now = new Date().getFullYear();
  return Array.from({ length: 80 }, (_, i) => now - i);
})();

/** A card's one-line summary once it has a product. */
const summaryOf = (item) =>
  [item.picked?.reference, item.picked?.brand, item.picked?.model]
    .filter(Boolean)
    .join(" · ") ||
  [item.brand, item.model, item.reference].filter(Boolean).join(" ") ||
  "Not identified yet";

/** What was HEARD for a card, so the rep can see what they are correcting. */
const heardOf = (item) =>
  [item.brand, item.model, item.reference].filter(Boolean).join(" ") || "—";

/**
 * Mandatory marker. Decorative — the blocked message below the button is what
 * actually says what is missing, and in words.
 */
const Req = () => (
  <sup className="rc-rdict__req" aria-hidden="true">*</sup>
);

/** What was actually written, in words — a count is what the rep will check. */
const createdSummary = (made) => {
  const parts = [];
  if (made.orderIds?.length) {
    parts.push(`${made.orderIds.length} order request${made.orderIds.length > 1 ? "s" : ""}`);
  }
  if (made.sellIds?.length) {
    parts.push(`${made.sellIds.length} sell request${made.sellIds.length > 1 ? "s" : ""}`);
  }
  if (made.pxRequest) parts.push("joined as a part exchange");
  return parts.join(", ");
};

const Marker = ({ done }) => (
  <span className={`rc-rdict__marker${done ? " is-done" : ""}`} aria-hidden="true">
    {done ? "✓" : "○"}
  </span>
);

/**
 * One watch. Collapsed to its summary when the product is confirmed; expanded
 * it carries the candidate picker and that side's own fields.
 */
const ItemCard = ({ item, index, total, onChange, openId, setOpenId }) => {
  const isOpen = openId === item.key;
  const isSell = item.side === "sell";
  const label = isSell
    ? `Sell request${total > 1 ? ` ${index + 1}/${total}` : ""}`
    : `Order product${total > 1 ? ` ${index + 1}/${total}` : ""}`;

  const set = (key) => (value) => onChange({ ...item, [key]: value });

  return (
    <div className={`rc-rdict__card${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        className="rc-rdict__card-head"
        aria-expanded={isOpen}
        onClick={() => setOpenId(isOpen ? null : item.key)}
      >
        <Marker done={!!item.picked} />
        <span className="rc-rdict__card-label">{label}</span>
        <span className="rc-rdict__card-summary">{summaryOf(item)}</span>
        <span className="rc-rdict__card-chev" aria-hidden="true">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="rc-rdict__card-body">
          {/* What speech produced, stated plainly: the picker below shows
              catalogue matches, and without this the rep cannot tell whether a
              wrong match came from a mishearing or a bad search. */}
          <p className="rc-rdict__heard">Heard: “{heardOf(item)}”</p>

          <label className="rc-rdict__label" htmlFor={`${item.key}-ref`}>
            Product<Req />
          </label>
          <ReferenceSelect
            id={`${item.key}-ref`}
            candidates={item.candidates}
            loose={item.looseCandidates}
            value={item.picked}
            onChange={(picked) => onChange({ ...item, picked })}
          />

          <div className="rc-rdict__grid">
            <div className="rc-rdict__field">
              <label className="rc-rdict__label" htmlFor={`${item.key}-year`}>Year<Req /></label>
              <select
                id={`${item.key}-year`}
                className="rc-rdict__select"
                value={item.year ?? ""}
                onChange={(e) => set("year")(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— still needed —</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Chips, not a native multi-select. A <select multiple> shows a
                scrolling box whose selection is invisible until you look twice,
                needs ctrl-click nobody discovers, and is close to unusable on a
                touch screen. Toggles say what is chosen at a glance and cost one
                tap each.
                The ONE asymmetry between the sides is real: the order form
                renders condition as isMulti (a buyer accepts several), the sell
                form as a single select — so these toggle, or replace. */}
            <div className="rc-rdict__field rc-rdict__field--wide">
              <span className="rc-rdict__label" id={`${item.key}-cond`}>
                Condition<Req />
                {!isSell && <span className="rc-rdict__hint"> — pick any that suit</span>}
              </span>
              <div className="rc-rdict__chips" role="group" aria-labelledby={`${item.key}-cond`}>
                {conditionOptions.map((o) => {
                  const on = item.condition.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`rc-rdict__chip${on ? " is-on" : ""}`}
                      aria-pressed={on}
                      onClick={() =>
                        set("condition")(
                          isSell
                            ? (on ? [] : [o.id])
                            : (on
                                ? item.condition.filter((c) => c !== o.id)
                                : [...item.condition, o.id])
                        )
                      }
                    >
                      {o.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {isSell && (
              <div className="rc-rdict__field">
                <label className="rc-rdict__label" htmlFor={`${item.key}-box`}>
                  Delivery set<Req />
                </label>
                <select
                  id={`${item.key}-box`}
                  className="rc-rdict__select"
                  value={item.deliverySet ?? ""}
                  onChange={(e) => set("deliverySet")(e.target.value || null)}
                >
                  <option value="">— still needed —</option>
                  {boxOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Optional by instruction — captured when spoken, never demanded,
                and never marked "still needed". */}
            {isSell ? (
              <div className="rc-rdict__field">
                <label className="rc-rdict__label" htmlFor={`${item.key}-price`}>
                  Price expectation
                </label>
                <input
                  id={`${item.key}-price`}
                  className="rc-rdict__select"
                  type="number"
                  inputMode="decimal"
                  placeholder="Optional"
                  value={item.priceExpectation ?? ""}
                  onChange={(e) =>
                    set("priceExpectation")(e.target.value === "" ? null : Number(e.target.value))
                  }
                />
              </div>
            ) : (
              <>
                <div className="rc-rdict__field">
                  <label className="rc-rdict__label" htmlFor={`${item.key}-min`}>Min budget</label>
                  <input
                    id={`${item.key}-min`}
                    className="rc-rdict__select"
                    type="number"
                    inputMode="decimal"
                    placeholder="Optional"
                    value={item.minBudget ?? ""}
                    onChange={(e) =>
                      set("minBudget")(e.target.value === "" ? null : Number(e.target.value))
                    }
                  />
                </div>
                <div className="rc-rdict__field">
                  <label className="rc-rdict__label" htmlFor={`${item.key}-max`}>Max budget</label>
                  <input
                    id={`${item.key}-max`}
                    className="rc-rdict__select"
                    type="number"
                    inputMode="decimal"
                    placeholder="Optional"
                    value={item.maxBudget ?? ""}
                    onChange={(e) =>
                      set("maxBudget")(e.target.value === "" ? null : Number(e.target.value))
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const RequestDictationReview = ({ result, onCreate, creating, createError, created, onView }) => {
  const [client, setClient] = useState(null);
  const [intent, setIntent] = useState(null);
  const [orders, setOrders] = useState([]);
  const [sells, setSells] = useState([]);
  const [openId, setOpenId] = useState(null);

  const { user } = useContext(UserContext) ?? {};
  const heardName = result?.clientName ?? null;
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoPicked, setAutoPicked] = useState(false);
  // Once the rep has touched the picker themselves, a later analysis must not
  // overwrite their choice — re-reading an edited transcript re-runs this
  // lookup, and silently replacing a person they chose by hand would be the
  // same wrong-client failure by a different route.
  const chosenByRep = useRef(false);

  // Look up the name that was heard, so the rep lands on the matches instead of
  // retyping what they just said. Only ever a SUGGESTION: see rankClientMatches
  // for when it is allowed to select outright.
  useEffect(() => {
    setMatches([]);
    setAutoPicked(false);
    if (!heardName) return undefined;

    const controller = new AbortController();
    let live = true;
    setSearching(true);

    searchClients(heardName, { signal: controller.signal })
      .then((rows) => {
        if (!live) return;
        const ranked = rankClientMatches(heardName, rows);
        setMatches(ranked.matches);
        if (ranked.auto && !chosenByRep.current) {
          setClient({ id: ranked.auto.id, name: personName(ranked.auto), type: ranked.auto.type });
          setAutoPicked(true);
        }
      })
      .finally(() => {
        if (live) setSearching(false);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [heardName]);

  // Says which of the four things happened, in words. A silently pre-filled
  // picker is indistinguishable from one the rep filled themselves, and that is
  // precisely the confusion that makes a wrong client slip through.
  const clientNote = () => {
    if (!heardName) return "Nobody was named. Search for the client, or add a new one.";
    if (searching) return `Looking for “${heardName}”…`;
    if (autoPicked && client?.id) {
      return `Auto-selected ${client.name} for “${heardName}” — pick another match or search if that is the wrong person.`;
    }
    if (matches.length > 1) return `${matches.length} people match “${heardName}” — pick the right one.`;
    if (matches.length === 1) return `One possible match for “${heardName}” — check it is the right person.`;
    return `Nobody matches “${heardName}”. Search, assign it to yourself, or add a new client.`;
  };

  // Re-seed whenever a NEW analysis arrives — editing the transcript re-runs
  // extraction, and the cards must follow the latest reading rather than keep
  // the shape of a sentence the rep has already corrected.
  useEffect(() => {
    setOrders(result?.orders ?? []);
    setSells(result?.sells ?? []);
    // Unset when the model was not sure AND the shape did not settle it — the
    // rep picks rather than the sheet guessing. Booking a buy as a sell is the
    // most expensive mistake available here.
    setIntent(result?.intentAgrees === false ? null : (result?.intent ?? null));
    // Open the first card that still needs a product, so the sheet lands on the
    // work rather than on a closed list.
    const first = [...(result?.orders ?? []), ...(result?.sells ?? [])].find((i) => !i.picked);
    setOpenId(first?.key ?? null);
  }, [result]);

  const replace = (list, setList) => (next) =>
    setList(list.map((i) => (i.key === next.key ? next : i)));

  const needsProduct = useMemo(
    () => [...orders, ...sells].filter((i) => !i.picked).length,
    [orders, sells]
  );

  const wantsOrders = intent === INTENTS.ORDER || intent === INTENTS.PX;
  const wantsSells = intent === INTENTS.SELL || intent === INTENTS.PX;

  const blocked =
    !client?.id
      ? "Assign a client first — the request is raised in their name."
      : !intent
        ? "Choose what kind of deal this is."
        : wantsOrders && orders.length === 0
          ? "Nothing to buy was heard. Record again, or choose a different kind of deal."
          : wantsSells && sells.length === 0
            ? "Nothing to sell was heard. Record again, or choose a different kind of deal."
            : needsProduct
              ? `Confirm the product on ${needsProduct} ${needsProduct === 1 ? "card" : "cards"}.`
              : null;

  // Same shape the listing sheet ends on: the sheet stays open, says what it
  // made, and lets the rep go there or close. Navigating away on success took
  // the rep somewhere they had not asked to go and lost the sheet.
  if (created) {
    return (
      <div className="rc-rdict__done" role="status">
        <p className="rc-rdict__done-title">Created!</p>
        <p className="rc-rdict__done-note">{createdSummary(created)}</p>
        <button type="button" className="rc-dictate-btn-primary" onClick={onView}>
          View request
        </button>
      </div>
    );
  }

  return (
    <div className="rc-rdict">
      {/* 1 — CLIENT. First, because the flow puts it first and because a
          request cannot exist without one. */}
      <section className="rc-rdict__section">
        <h3 className="rc-rdict__section-title">
          <Marker done={!!client?.id} /> Client
        </h3>
        <AssignPersonPicker
          suggestions={matches}
          suggestionsNote={clientNote()}
          value={client}
          onChange={(picked) => {
            chosenByRep.current = true;
            setAutoPicked(false);
            setClient(picked ?? null);
          }}
          createTitle="New Client"
        />
        {/* The fallback when nobody matches. A BUTTON, never automatic:
            silently raising the request in the rep's own name would seed the
            CRM with staff standing in for clients nobody can later untangle. */}
        {!client?.id && user?.id && (
          <button
            type="button"
            className="rc-rdict__self"
            onClick={() => {
              chosenByRep.current = true;
              setAutoPicked(false);
              setClient({ id: user.id, name: personName(user) || "Me", type: user.type });
            }}
          >
            Assign to me ({personName(user) || "me"})
          </button>
        )}
      </section>

      {/* 2 — WHAT KIND OF DEAL. Above the products because it decides which
          product lists even apply. */}
      <section className="rc-rdict__section">
        <h3 className="rc-rdict__section-title">
          <Marker done={!!intent} /> Kind of deal
          {result?.intentAgrees === false && (
            <span className="rc-rdict__heard-tag">heard, not typed</span>
          )}
        </h3>

        {result?.intentAgrees === false && (
          <p className="rc-rdict__warn" role="note">
            What was said and what was heard disagree — it sounded like a{" "}
            {INTENT_LABELS[result.statedIntent]?.toLowerCase()}, but a{" "}
            {INTENT_LABELS[result.shapeIntent]?.toLowerCase()} was extracted. Pick
            the right one.
          </p>
        )}

        <div className="rc-rdict__intent" role="group" aria-label="Kind of deal">
          {[INTENTS.ORDER, INTENTS.SELL, INTENTS.PX].map((key) => (
            <button
              key={key}
              type="button"
              className={`rc-rdict__intent-btn${intent === key ? " is-active" : ""}`}
              aria-pressed={intent === key}
              onClick={() => setIntent(key)}
            >
              {INTENT_LABELS[key]}
            </button>
          ))}
        </div>
      </section>

      {/* 3 — THE PRODUCTS, one card each. */}
      {wantsOrders && (
        <section className="rc-rdict__section">
          <h3 className="rc-rdict__section-title">Looking to buy ({orders.length})</h3>
          {orders.map((item, i) => (
            <ItemCard
              key={item.key}
              item={item}
              index={i}
              total={orders.length}
              onChange={replace(orders, setOrders)}
              openId={openId}
              setOpenId={setOpenId}
            />
          ))}
        </section>
      )}

      {wantsSells && (
        <section className="rc-rdict__section">
          <h3 className="rc-rdict__section-title">Looking to sell ({sells.length})</h3>
          {sells.map((item, i) => (
            <ItemCard
              key={item.key}
              item={item}
              index={i}
              total={sells.length}
              onChange={replace(sells, setSells)}
              openId={openId}
              setOpenId={setOpenId}
            />
          ))}
        </section>
      )}

      {/* Watches heard but past the flow's own cap. Named, never swallowed —
          a watch the rep said out loud going missing with no word about it is
          the worst failure this feature has available. */}
      {result?.dropped?.length > 0 && (
        <p className="rc-rdict__warn" role="note">
          Only {MAX_PER_SIDE} per side can go on one request, so these were left
          out: {result.dropped.join(", ")}. Add them in the flow afterwards.
        </p>
      )}

      {createError && (
        <p className="rc-rdict__error" role="alert">{createError}</p>
      )}
      {blocked && !createError && (
        <p className="rc-rdict__blocked">{blocked}</p>
      )}

      <div className="rc-rdict__actions">
        <button
          type="button"
          className="rc-dictate-btn-primary"
          disabled={!!blocked || creating}
          onClick={() => onCreate({ client, intent, orders, sells })}
        >
          {/* The button names the outcome, so the last thing clicked states
              what it is about to create. */}
          {creating ? "Creating…" : (INTENT_ACTION[intent] ?? "Create request")}
        </button>
      </div>
    </div>
  );
};

export default RequestDictationReview;
